<template>
  <div :class="type" class="product">
    <div class="yellow-line-1"></div>
    <div class="yellow-line-2"></div>
    <div class="yellow-line-3"></div>
    <div class="yellow-line"></div>
    <img class="role-kaka" src="@/assets/imgs/optimize/role-kaka.png" alt="role-kaka">
    <div class="product-content">
      <div class="product-title">
        {{ $t('payment.product.title') }}
      </div>
      <div class="product-list">
        <ProductItem
          v-for="item in state.products"
          :key="item.productCode"
          :item="item"
          :active="modelValue === item.productCode"
          @select="selectProduct"
        />
      </div>
      <div class="summary-wrap">
        <div class="title">
          {{ $t('payment.product.summary') }}
        </div>
        <div class="product-info">
          {{ $t('payment.product.selected') }}:
          {{ localeFormatNumber(state.selectedProduct?.coin) }} {{ $t('payment.product.diamond') }}
          <span class="gift-coin" v-if="state.selectedProduct?.giftCoin">
            ,{{ $t('payment.product.gift') }} {{ localeFormatNumber(state.selectedProduct?.giftCoin) }} {{ $t('payment.product.diamond') }}
          </span>
          <span class="points" v-if="state.selectedProduct?.points">
            ,{{ $t('payment.product.gift') }} {{ localeFormatNumber(state.selectedProduct?.points) }} {{ $t('payment.product.points') }}
          </span>
        </div>
        <div class="price-wrap">
          <span class="total">{{ $t('payment.product.total') }}: </span>
          <span class="currency">{{ state.selectedProduct?.currency }}</span>
          <span class="price">{{ calcProductPrice(state.selectedProduct?.price) }}</span>
        </div>
        <div class="pay-btn btn" @click="$emit('confirm')">
          {{ $t('payment.product.confirm') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { isMobile } from '@/utils'
import { reactive, computed, defineProps, defineEmits } from 'vue'
import { formatNumber } from '@/utils'
import { useStore } from 'vuex'
import ProductItem from './ProductItem/index.vue'
const store = useStore()
const locale = computed(() => store.state.locale)
const type = isMobile ? 'mobile' : 'pc'

const localeFormatNumber = (number) => {
  return formatNumber(number, locale.value)
}

const props = defineProps({
  products: {
    type: Array,
    default: () => []
  },
  modelValue: {
    type: String,
    default: ''
  }
})

const DIAMOND_PROP_IDS = ['1', '70301030']

const state = reactive({
  products: computed(() => {
    return props.products.map(item => {
      const isValidActivity = checkIsValidActivity(item)
      const showTag = getShowTag(item, isValidActivity)
      const coin = item.coin
      const regularGiftCoin = item.giftCoin || 0
      const activityGiftCoin = getActivityGiftCoin(item, isValidActivity)
      const points = getPoints(coin)
      const {limitSwitch, limitCount} = getLimit(item, isValidActivity)
      return {
        ...item,
        isValidActivity,
        showTag,
        coin,
        regularGiftCoin,
        activityGiftCoin,
        // 兼容旧字段：汇总场景如需总赠送可用
        giftCoin: regularGiftCoin + activityGiftCoin,
        points,
        isLimit: limitSwitch === 2 && limitCount > 0,
        limitSwitch,
        limitCount
      }
    })
  }),
  selectedProduct: computed(() => {
    return state.products.find(item => item.productCode === props.modelValue)
  }),
})

const checkIsValidActivity = (item) => {
  const activity = item.activity
  if (!activity) {
    return false
  }
  if (activity.activityType !== 1) { // 枪战只支持1首充加赠
    return false
  }
  if (activity.activityDurationType === 1) { // 1限时 2常驻
    const now = Math.floor(Date.now() / 1000)
    if (now >= activity.activityEndTime || now < activity.activityStartTime) { // 不在活动时间内
      return false
    }
  }
  if (activity.limitSwitch === 2) { // 1不限购 2限购
    if (activity.limitCount <= 0) { // 限购次数
      return false
    }
  }
  return true
}

const getShowTag = (item, isValidActivity) => {
  if (!isValidActivity) {
    return item.strategyType === 1 && item.giftTagShowSwitch === 2 && item.giftCoin > 0
  }
  const activity = item.activity
  if (activity.tagShowSwitch === 2) { // 1不显示 2显示
    return true
  }
  return false
}

// 活动赠送：解析 activity.propsGift 中彩钻数量
const getActivityGiftCoin = (item, isValidActivity) => {
  if (!isValidActivity) {
    return 0
  }
  try {
    const propsGift = JSON.parse(item.activity.propsGift || '[]')
    if (!Array.isArray(propsGift)) {
      return 0
    }
    return propsGift.reduce((sum, gift) => {
      if (gift.type === 1 && DIAMOND_PROP_IDS.includes(String(gift.id))) {
        return sum + (gift.count || 0)
      }
      return sum
    }, 0)
  } catch (e) {
    return 0
  }
}

// 积分：购买彩钻 1:1 计分（赠送彩钻不计）；VIP 额外分由会员档 extraPointsPer100 决定，此处仅展示基础分
const getPoints = (coin) => {
  return Math.floor(coin || 0)
}

const getLimit = (item, isValidActivity) => {
  if (!isValidActivity) {
    return { limitSwitch: item.limitSwitch, limitCount: item.limitCount }
  }
  const activity = item.activity
  return { limitSwitch: activity.limitSwitch, limitCount: activity.limitCount }
}

const emit = defineEmits(['update:modelValue', 'confirm'])

const calcProductPrice = (price) => {
  return localeFormatNumber(price / 100)
}

const selectProduct = (product) => {
  if (props.modelValue === product.productCode) {
    return
  }
  emit('update:modelValue', product.productCode)
}
</script>

<style src="./pc_style.less" lang="less" scoped></style>
<style src="./mobile_style.less" lang="less" scoped></style>
